import type { ReactElement } from 'react';
import { Children, cloneElement, useEffect, useState } from 'react';
import { vars } from '@etg/wings';
import { ContentWrapper } from '@eti/components';
import { css, cx, etiColors, keyframes, mediaQueries } from '@eti/styles';
import { useDirection } from '@eti/providers';

const sectionStyles = (isMounted: boolean) => css`
  background-color: #e5e5e5;
  opacity: ${isMounted ? 1 : 0};
  transition: opacity 1s ease-in-out;
`;

const wrapperStyles = (hasBackgroundColor: boolean) => css`
  background: ${hasBackgroundColor ? vars.colors.header.base : etiColors.grayScale[4]};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 8px;

  @media ${mediaQueries.medium.up} {
    background: ${hasBackgroundColor
      ? `linear-gradient(${vars.colors.footer.background} 86%, ${etiColors.grayScale[4]} 35%)`
      : etiColors.grayScale[4]};
    max-width: none;
    padding: 16px;
    padding-block-start: 24px;
  }
`;

const shine = (isRTL: boolean) => keyframes`
  0% {
    ${isRTL ? 'right: -400px;' : 'left: -400px;'}
  }
  100% {
    ${isRTL ? 'right: 600px;' : 'left: 600px;'}
  }
`;

const boxStyles = (backgroundColor: string, isRTL: boolean, height?: number, width?: number) => css`
  ${`background-color: ${backgroundColor || '#e5e5e5;'}`};

  height: ${`${height}px`};
  overflow: hidden;
  position: relative;
  width: ${width ? `${width}%` : '100%'};

  &::before {
    animation: ${`${shine(isRTL)} 1.5s infinite;`};
    background-image: linear-gradient(to right, #e5e5e500 0%, #fffc 50%, #e5e5e500 100%);
    background-repeat: repeat-x;
    content: '';
    height: 100%;
    position: absolute;
    transform: skewX(-45deg);
    width: 200px;
  }
`;

interface SectionProps {
  children: ReactElement;
}
const Section = ({ children }: SectionProps) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
    }
  }, [isMounted]);

  return (
    <section className={sectionStyles(isMounted)} data-testid="resultPage-skeleton">
      {children}
    </section>
  );
};

interface BoxProps {
  backgroundColor?: string;
  children?: ReactElement;
  height: number;
  marginBottom?: number;
  width?: number;
}
const Box = ({
  children,
  marginBottom = 20,
  backgroundColor = `${vars.colors.page.background.primary}`,
  height,
  width,
}: BoxProps) => {
  const { isRTL } = useDirection();

  if (!children) {
    return (
      <div
        className={boxStyles(backgroundColor, isRTL, height, width)}
        data-testid="skeleton-box-without-children"
      />
    );
  }

  return (
    <>
      <div className={boxStyles(backgroundColor, isRTL, height, width)} />
      {Children.map(children, (child) => cloneElement(child, { margin: marginBottom / 2 }))}
    </>
  );
};

interface WrapperProps {
  children: ReactElement;
  className?: string;
  hasBackgroundColor?: boolean;
}
const Wrapper = ({ className, children, hasBackgroundColor = false }: WrapperProps) => (
  <ContentWrapper className={cx(wrapperStyles(hasBackgroundColor), className)}>
    {children}
  </ContentWrapper>
);

export default {
  Box,
  Wrapper,
  Section,
};
